import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, OpinionsSectionContent } from "api/shop";
import {
  HeadingTagSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaTextarea,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MdAdd, MdDelete } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

const FORM_NAMES = {
  ANIMATION: "animation",
  HEADER_TEXT: "headerText",
  HEADER_COLOR: "headerColor",
  HEADER_TAG: "headerTag",
  OPINIONS: "opinions",
} as const;

const opinionSchema = yup.object({
  name: yup.string().required("Imię jest wymagane"),
  opinion: yup.string().required("Opinia jest wymagana"),
  source: yup.string().default(""),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.HEADER_TEXT]: yup.string().required("Nagłówek jest wymagany"),
    [FORM_NAMES.HEADER_COLOR]: yup.string(),
    [FORM_NAMES.HEADER_TAG]: yup.string(),
    [FORM_NAMES.OPINIONS]: yup.array().of(opinionSchema).min(1, "Dodaj przynajmniej jedną opinię"),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormValues {
  animation: string;
  headerText: string;
  headerColor: string;
  headerTag: string;
  opinions: Array<{ name: string; opinion: string; source: string }>;
  margins: SectionMargins;
}

export interface OpinionsSectionFormValues {
  animation: string;
  header: { text: string; color?: string; tag?: string };
  opinions: Array<{ name: string; opinion: string; source: string }>;
  margins?: SectionMargins;
}

interface OpinionsSectionFormProps {
  onSubmit: (data: OpinionsSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: OpinionsSectionContent;
  isLoading?: boolean;
}

export const OpinionsSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: OpinionsSectionFormProps) => {
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.HEADER_TEXT]: defaultValues?.header?.text ?? "",
      [FORM_NAMES.HEADER_COLOR]: defaultValues?.header?.color ?? "",
      [FORM_NAMES.HEADER_TAG]: defaultValues?.header?.tag ?? "h2",
      [FORM_NAMES.OPINIONS]: defaultValues?.opinions ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;

  const {
    fields: opinionFields,
    append: appendOpinion,
    remove: removeOpinion,
  } = useFieldArray({
    control,
    name: FORM_NAMES.OPINIONS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const formattedData: OpinionsSectionFormValues = {
      animation: data.animation,
      header: {
        text: data.headerText,
        color: data.headerColor || undefined,
        tag: data.headerTag as any,
      },
      opinions: data.opinions,
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addOpinion = () => {
    appendOpinion({ name: "", opinion: "", source: "" });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaSelect name={FORM_NAMES.ANIMATION} label="Animacja" required options={ANIMATION_OPTIONS} />

        {/* Header */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <h4 className="mb-3 font-medium text-slate-700 dark:text-slate-300">Nagłówek</h4>
          <div className="grid gap-3 md:grid-cols-2">
            <PaziaInput name={FORM_NAMES.HEADER_TEXT} label="Tekst nagłówka" required />
            <PaziaInput name={FORM_NAMES.HEADER_COLOR} label="Kolor nagłówka" placeholder="#000000" />
          </div>
          <div className="mt-3">
            <HeadingTagSelect name={FORM_NAMES.HEADER_TAG} />
          </div>
        </div>

        {/* Opinions Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Opinie</h4>
            <button
              type="button"
              onClick={addOpinion}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj opinię
            </button>
          </div>

          {opinionFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak opinii. Kliknij "Dodaj opinię", aby dodać.
            </p>
          ) : (
            <div className="space-y-4">
              {opinionFields.map((field, index) => (
                <div
                  key={field.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800"
                >
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
                      Opinia #{index + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeOpinion(index)}
                      className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń opinię"
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                  <div className="grid gap-3">
                    <div className="grid gap-3 md:grid-cols-2">
                      <PaziaInput
                        name={`${FORM_NAMES.OPINIONS}.${index}.name`}
                        label="Imię i nazwisko"
                        required
                      />
                      <PaziaInput
                        name={`${FORM_NAMES.OPINIONS}.${index}.source`}
                        label="Źródło"
                        placeholder="np. Google, Facebook"
                      />
                    </div>
                    <PaziaTextarea
                      name={`${FORM_NAMES.OPINIONS}.${index}.opinion`}
                      label="Treść opinii"
                      rows={3}
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};
